<template>
    <div class="relative overflow-auto">
        <div :class="carouselBaseClasses" ref="scrollContainer" @mousedown="onMouseDown">
            <slot></slot>
            <div data-type="spacer" ref="spacer" class="h-1 shrink-0" :class="[isCarouselActive ? 'w-12' : 'w-4']"></div>
        </div>
        <div class="flex flex-row gap-1 items-center justify-center pb-4" v-if="isCarouselActive">
            <div
                v-for="(active, index) in navigationDots"
                :key="index"
                :class="['w-2 aspect-square rounded-full transition-colors duration-300', activeNavigationDotIndex === index ? 'bg-system-dark' : 'bg-transparent-dark-10']"
            ></div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, onUnmounted } from 'vue'

    // Classes
    const carouselBaseClasses = computed(() => {
        const baseClasses = ['w-full flex gap-2 overflow-x-auto pt-4 pb-4 select-none scrollbar-hide']
        const slideClasses = ['[&>*]:snap-start [&>*]:scroll-ml-6 [&>*]:shrink-0 [&>*]:relative [&>*:first-child]:ml-6']
        const snapClasses = isMouseDown.value ? 'snap-none ' : 'snap-x snap-mandatory'
        const cursorClasses = isCarouselActive.value ? (isMouseDown.value ? 'cursor-grabbing' : 'cursor-grab') : ''

        return [baseClasses, slideClasses, snapClasses, cursorClasses]
    })

    // Carousel logic
    const scrollContainer = ref<HTMLDivElement | null>(null)
    const spacer = ref<HTMLDivElement | null>(null)
    const isMouseDown = ref(false)
    const startX = ref<number>(0)
    const scrollLeft = ref<number>(0)
    const navigationDots = ref<boolean[]>([])
    const activeNavigationDotIndex = ref(0)
    const isCarouselActive = ref(false)

    // Constants
    const LEFT_PADDING = 24
    const RIGHT_EXTRA_PADDING = 16

    onMounted(() => {
        observeSlides()
        observeCarousel()
        if (scrollContainer.value) scrollContainer.value.addEventListener('scroll', handleScroll)
    })

    onUnmounted(() => {
        if (scrollContainer.value) scrollContainer.value.removeEventListener('scroll', handleScroll)
    })

    // Calculate which navigation dot is active
    const handleScroll = () => {
        const { slidePositionsWithPadding } = calculateSlidePositions()
        const scrollPosition = scrollContainer.value!.scrollLeft
        activeNavigationDotIndex.value = calculateActiveNavigationDotIndex(slidePositionsWithPadding, scrollPosition)
    }

    // Observe carousel size
    const observeCarousel = () => {
        if (scrollContainer.value) {
            const resizeObserver = new ResizeObserver((entries) => {
                if (!scrollContainer.value) return
                const scrollWidth = scrollContainer.value.scrollWidth
                const clientWidth = scrollContainer.value.clientWidth
                const spacerWidth = spacer.value?.clientWidth ?? 0
                const carouselWidth = scrollWidth - spacerWidth + RIGHT_EXTRA_PADDING

                const carouselIsOverflowing = carouselWidth > clientWidth
                isCarouselActive.value = carouselIsOverflowing
            })
            resizeObserver.observe(scrollContainer.value)
        }
    }

    // Observe visiblity slides
    const observeSlides = () => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!scrollContainer.value) return
                    // Find the index of the entry in the slides list
                    const index = slides.findIndex((slide) => slide === entry.target)
                    if (index !== -1) {
                        const isFullyVisible = entry.intersectionRatio === 1
                        //const isPartlyVisible = entry.isIntersecting

                        if (isFullyVisible) {
                            navigationDots.value[index] = true
                        } else {
                            navigationDots.value[index] = false
                        }
                    }
                })
            },
            {
                root: scrollContainer.value,
                threshold: [0, 1]
            }
        )

        const slides: HTMLElement[] = Array.from(scrollContainer.value?.querySelectorAll(':scope > *:not([data-type="spacer"])') ?? []).filter(
            (el): el is HTMLElement => el instanceof HTMLElement
        )

        slides.forEach((slide) => {
            navigationDots.value.push(false)
            observer.observe(slide)
        })
    }

    // Dragging functions
    const onMouseDown = (e: MouseEvent) => {
        if (!isCarouselActive.value) return
        if (scrollContainer.value) {
            isMouseDown.value = true
            startX.value = e.pageX - scrollContainer.value.offsetLeft
            scrollLeft.value = scrollContainer.value.scrollLeft
            document.addEventListener('mousemove', onMouseMove)
            document.addEventListener('mouseup', onMouseUp)
            scrollContainer.value.addEventListener('mouseleave', onMouseUp)
        }
    }

    const onMouseMove = (e: MouseEvent) => {
        if (!isMouseDown.value || !scrollContainer.value) return
        const x = e.pageX - scrollContainer.value.offsetLeft
        const walk = (x - startX.value) * 2 // scroll speed multiplier
        scrollContainer.value.scrollLeft = scrollLeft.value - walk
    }

    const onMouseUp = () => {
        if (scrollContainer.value) {
            document.removeEventListener('mousemove', onMouseMove)
            document.removeEventListener('mouseup', onMouseUp)
            scrollContainer.value.removeEventListener('mouseleave', onMouseUp)

            const { slidePositions } = calculateSlidePositions()
            const scrollPosition = scrollContainer.value.scrollLeft
            const newScrollPosition = calculateClosestSlideToScrollTo(slidePositions, scrollPosition)
            const maxScrollPosition = scrollContainer.value.scrollWidth - scrollContainer.value.clientWidth

            if (scrollPosition === 0 || scrollPosition >= maxScrollPosition || scrollPosition === newScrollPosition) {
                isMouseDown.value = false
            } else {
                // TODO: Safari doesn't scrollTo on mouseleave yet, unclear why.
                scrollContainer.value.scrollTo({
                    left: newScrollPosition,
                    behavior: 'smooth'
                })
                // scrollContainer.value.scrollLeft = newScrollPosition
                scrollContainer.value.addEventListener('scrollend', endScroll)
            }
        }
    }

    const endScroll = () => {
        isMouseDown.value = false
        scrollContainer.value!.removeEventListener('scrollend', endScroll)
    }

    // Helper functions
    const calculateSlidePositions = () => {
        const slides: HTMLElement[] = Array.from(scrollContainer.value?.querySelectorAll(':scope > *:not([data-type="spacer"])') ?? []).filter(
            (el): el is HTMLElement => el instanceof HTMLElement
        )
        const slidePositions = slides.map((slide: HTMLElement) => slide.offsetLeft - LEFT_PADDING)
        const slidePositionsWithPadding = slides.map((slide: HTMLElement) => slide.offsetLeft)
        return { slidePositions, slidePositionsWithPadding }
    }

    const calculateActiveNavigationDotIndex = (slidePositions: number[], scrollPosition: number): number => {
        return slidePositions.findIndex((slidePosition) => slidePosition >= scrollPosition)
    }

    const calculateClosestSlideToScrollTo = (slidePositions: number[], scrollPosition: number): number => {
        return slidePositions.reduce((prev, curr) => (Math.abs(curr - scrollPosition) < Math.abs(prev - scrollPosition) ? curr : prev))
    }
</script>

<style scoped>
    .scrollbar-hide::-webkit-scrollbar {
        display: none;
    }

    /* For IE, Edge and Firefox */
    .scrollbar-hide {
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */
    }
</style>
